import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7adon641prrpukdf4fg';

export default function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setStatus('sending');
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        message: formData.message,
      });
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-14">
          <p className="text-xs font-semibold tracking-widest text-emerald-600 uppercase mb-3">Get in Touch</p>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
            Contact Us
          </h2>
          <p className="text-gray-500 mt-4 text-base max-w-lg">
            Have a question or want to learn more about our products? We&apos;d love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <div className="flex flex-col gap-8">
            <div className="bg-[#F7F7F5] rounded-3xl p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Contact Information</h3>
              <ul className="flex flex-col gap-5">
                <li className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Address</p>
                    <p className="text-gray-800 font-medium text-sm">Kafantari 7</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 flex-shrink-0">
                    <i className="ri-building-line text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">City</p>
                    <p className="text-gray-800 font-medium text-sm">Agioi Anargyroi</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 flex-shrink-0">
                    <i className="ri-mail-line text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">ZIP</p>
                    <p className="text-gray-800 font-medium text-sm">13561</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 flex-shrink-0">
                    <i className="ri-global-line text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Country</p>
                    <p className="text-gray-800 font-medium text-sm">Greece</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 flex-shrink-0">
                    <i className="ri-mail-send-line text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Email</p>
                    <p className="text-gray-800 font-medium text-sm">info@amikpc.com</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 flex-shrink-0">
                    <i className="ri-phone-line text-base" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Number</p>
                    <p className="text-gray-800 font-medium text-sm">+1 7407373554</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <form
              data-readdy-form
              id="amikpc-contact-form"
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div>
                <label htmlFor="name" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors bg-[#F7F7F5]"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors bg-[#F7F7F5]"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How can we help you?"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors bg-[#F7F7F5] resize-none"
                />
                <p className={`text-xs mt-1 text-right ${charCount >= 500 ? 'text-rose-500' : 'text-gray-400'}`}>
                  {charCount}/500
                </p>
              </div>

              {status === 'success' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-emerald-700 text-sm font-medium">
                  Message sent successfully! We&apos;ll get back to you soon.
                </div>
              )}
              {status === 'error' && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-rose-700 text-sm font-medium">
                  Something went wrong. Please try again.
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'sending' || charCount > 500}
                className="bg-gray-900 text-white font-semibold text-sm rounded-xl px-6 py-3.5 hover:bg-gray-700 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
